import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Card,
  Stack,
  Button,
  Title,
  Divider,
  Stepper,
  Group,
  Alert,
  Anchor,
  Text,
} from "@mantine/core";
import posthog from "posthog-js";
import BillingInformation from "./BillingInformation";
import TicketsForm from "./TicketsForm";
import ReviewStep from "./ReviewStep";
import SuccessPage from "./SuccessPage";
import resolver from "@congrs/resolver";
import { web_createRegistrationSchema } from "@congrs/schemas";
import { useCreateRegistration } from "../../../api/registrations";
import { useTranslation } from "react-i18next";
import { IconCheck, IconAlertCircle } from "@tabler/icons-react";

const SUPPORT_EMAIL = "support@congrs.com";

const STEPS = {
  BILLING: 0,
  TICKETS: 1,
  REVIEW: 2,
  COMPLETED: 3,
};

export default function RegistrationForm({
  eventId,
  inviteToken,
  customFields = [],
  ticketTypes = [],
  maxQuantity,
  eventLogo,
  eventName,
  successMessage,
  additionalInfo,
}) {
  const storageKey = `registration_${eventId}`;

  const [saved] = useState(() => {
    try {
      const item = sessionStorage.getItem(storageKey);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  });

  const [step, setStep] = useState(saved?.step ?? STEPS.BILLING);
  const hasStartedRef = useRef(false);

  const registrationMutation = useCreateRegistration();
  const topRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    posthog.capture("registration_form_opened", {
      event_id: eventId,
      event_name: eventName,
    });
  }, [eventId, eventName]);

  const schema = useMemo(() => {
    return web_createRegistrationSchema(customFields, {
      requireTicketType: ticketTypes.length > 0,
    });
  }, [customFields, ticketTypes]);

  const { control, handleSubmit, watch, getValues, trigger, reset } = useForm({
    defaultValues: saved?.formData ?? {
      eventId,
      type: "individual",
      firstName: "",
      lastName: "",
      email: "",
      companyName: "",
      vatNumber: "",
      tickets: [],
      additionalFields: {},
    },
    mode: "onChange",
    resolver: resolver(schema, { allErrors: true, strictTypes: false }),
  });

  const registrationType = watch("type");

  const saveToStorage = useCallback(
    (newStep, formData) => {
      if (newStep === STEPS.COMPLETED) return;
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ step: newStep, formData })
      );
    },
    [storageKey]
  );

  useEffect(() => {
    const subscription = watch((values) => {
      saveToStorage(step, values);
      if (!hasStartedRef.current) {
        hasStartedRef.current = true;
        posthog.capture("registration_form_started", {
          event_id: eventId,
          event_name: eventName,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, step, saveToStorage, eventId, eventName]);

  const goToStep = (newStep) => {
    setStep(newStep);
    saveToStorage(newStep, getValues());
    setTimeout(
      () =>
        topRef.current?.scrollIntoView({ behavior: "auto", block: "start" }),
      0
    );
  };

  const registrationCustomFields = useMemo(() => {
    return customFields.filter((f) => f.appliedTo === "registrations");
  }, [customFields]);

  const ticketCustomFields = useMemo(() => {
    return customFields.filter((f) => f.appliedTo === "ticket");
  }, [customFields]);

  // normalize additional fields to ensure all custom fields are present
  const normalizeAdditionalFields = useCallback((fields, customFieldsList) => {
    const normalized = {};
    customFieldsList.forEach((field) => {
      normalized[field.key] = fields?.[field.key] ?? null;
    });
    return normalized;
  }, []);

  // returns array of field names to validate for the current step
  const getStepFields = useCallback(
    (stepNumber) => {
      switch (stepNumber) {
        case STEPS.BILLING: {
          const billingFields = ["type", "email"];

          if (registrationType === "company") {
            billingFields.push("companyName");
          } else {
            billingFields.push("firstName", "lastName");
          }

          const requiredCustomFields = registrationCustomFields
            .filter((f) => f.required)
            .map((f) => `additionalFields.${f.key}`);

          return [...billingFields, ...requiredCustomFields];
        }

        case STEPS.TICKETS: {
          const tickets = getValues("tickets") || [];
          return tickets.flatMap((_, index) => [
            `tickets.${index}.firstName`,
            `tickets.${index}.lastName`,
            `tickets.${index}.email`,
            `tickets.${index}.ticketTypeId`,
            ...ticketCustomFields
              .filter((f) => f.required)
              .map((f) => `tickets.${index}.additionalFields.${f.key}`),
          ]);
        }

        default:
          return [];
      }
    },
    [registrationType, registrationCustomFields, ticketCustomFields, getValues]
  );

  const nextStep = async () => {
    const fieldsToValidate = getStepFields(step);

    if (step === STEPS.TICKETS) {
      const tickets = getValues("tickets") || [];
      if (tickets.length === 0) {
        return;
      }
    }

    const valid = await trigger(fieldsToValidate);
    if (valid) {
      goToStep(step + 1);
    }
  };

  const prevStep = () => {
    const currentValues = getValues();

    if (step === STEPS.TICKETS) {
      reset({
        eventId,
        type: currentValues.type,
        firstName: currentValues.firstName || "",
        lastName: currentValues.lastName || "",
        email: currentValues.email || "",
        companyName: currentValues.companyName || "",
        vatNumber: currentValues.vatNumber || "",
        tickets: [],
        additionalFields: currentValues.additionalFields || {},
      });
    }

    goToStep(step - 1);
  };

  const onSubmit = (data) => {
    const transformedData = {
      eventId: data.eventId,
      type: data.type,
      email: data.email,
      tickets: data.tickets.map((ticket) => {
        const { id, ...ticketWithoutId } = ticket;
        return {
          firstName: ticketWithoutId.firstName,
          lastName: ticketWithoutId.lastName,
          email: ticketWithoutId.email,
          ticketTypeId: ticketWithoutId.ticketTypeId
            ? Number(ticketWithoutId.ticketTypeId)
            : null,
          additionalFields: normalizeAdditionalFields(
            ticketWithoutId.additionalFields,
            ticketCustomFields
          ),
        };
      }),
    };

    if (data.type === "individual") {
      transformedData.firstName = data.firstName;
      transformedData.lastName = data.lastName;
    } else if (data.type === "company") {
      transformedData.companyName = data.companyName;
      if (data.vatNumber) {
        transformedData.vatNumber = data.vatNumber;
      }
    }

    transformedData.additionalFields = normalizeAdditionalFields(
      data.additionalFields,
      registrationCustomFields
    );

    if (inviteToken) {
      transformedData.inviteToken = inviteToken;
    }

    registrationMutation.mutate(transformedData, {
      onSuccess: () => {
        sessionStorage.removeItem(storageKey);
        sessionStorage.removeItem(`landing_${eventId}`);
        setStep(STEPS.COMPLETED);
        posthog.capture("registration_form_completed", {
          event_id: eventId,
          event_name: eventName,
          tickets_count: transformedData.tickets.length,
          registration_type: transformedData.type,
        });
        setTimeout(
          () =>
            topRef.current?.scrollIntoView({
              behavior: "auto",
              block: "start",
            }),
          0
        );
      },
    });
  };

  // The API returns readable, actionable messages on 4xx (capacity reached,
  // duplicate attendee email, registration closed). Surface those verbatim.
  // 5xx and network failures get the generic message only.
  const submitError = registrationMutation.error;
  const submitErrorDetail =
    submitError?.status >= 400 && submitError?.status < 500
      ? submitError.message
      : null;

  const renderActionButton = () => {
    if (step < STEPS.REVIEW) {
      return <Button onClick={nextStep}>{t("next")}</Button>;
    }

    if (step === STEPS.REVIEW) {
      return (
        <Button
          onClick={handleSubmit(onSubmit)}
          loading={registrationMutation.isPending}
        >
          {t("submit_registration")}
        </Button>
      );
    }

    return null;
  };

  return (
    <Card ref={topRef} shadow="sm" p="lg" radius="md" withBorder>
      <Stack gap="md">
        {step !== STEPS.COMPLETED && (
          <Title order={3}>{t("event_registration")}</Title>
        )}

        <Stepper
          active={step}
          breakpoint="sm"
          styles={
            step === STEPS.COMPLETED ? { steps: { display: "none" } } : {}
          }
        >
          <Stepper.Step
            label={t("registration")}
            description={t("billing_information_step")}
            completedIcon={<IconCheck size={32} />}
          >
            <BillingInformation
              control={control}
              registrationType={registrationType}
              registrationCustomFields={registrationCustomFields}
            />
          </Stepper.Step>

          <Stepper.Step
            label={t("tickets")}
            description={t("attendee_details")}
            completedIcon={<IconCheck size={32} />}
          >
            <TicketsForm
              control={control}
              ticketTypes={ticketTypes}
              ticketCustomFields={ticketCustomFields}
              maxQuantity={maxQuantity}
            />
          </Stepper.Step>

          <Stepper.Step
            label={t("review")}
            description={t("confirm_submit")}
            completedIcon={<IconCheck size={32} />}
          >
            <ReviewStep
              formData={getValues()}
              ticketTypes={ticketTypes}
              customFields={customFields}
            />
          </Stepper.Step>

          <Stepper.Completed>
            <SuccessPage
              eventLogo={eventLogo}
              eventName={eventName}
              successMessage={successMessage}
              additionalInfo={additionalInfo}
            />
          </Stepper.Completed>
        </Stepper>

        {step !== STEPS.COMPLETED && (
          <>
            {registrationMutation.isError && (
              <Alert
                color="red"
                variant="light"
                icon={<IconAlertCircle size={18} />}
                title={t(
                  "registration_submit_error_title",
                  "Something went wrong"
                )}
              >
                <Stack gap={4}>
                  {submitErrorDetail && (
                    <Text size="sm">{submitErrorDetail}</Text>
                  )}
                  <Text size="sm">
                    {t(
                      "registration_submit_error_message",
                      "We couldn't submit your registration. Please try again, and contact our support if the problem persists."
                    )}
                  </Text>
                  <Anchor size="sm" href={`mailto:${SUPPORT_EMAIL}`}>
                    {SUPPORT_EMAIL}
                  </Anchor>
                </Stack>
              </Alert>
            )}

            <Divider />

            <Group
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "space-between",
              }}
            >
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={step === STEPS.BILLING}
              >
                {t("back")}
              </Button>

              {renderActionButton()}
            </Group>
          </>
        )}
      </Stack>
    </Card>
  );
}
